"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils"; // Import cn for conditional class names

interface CoverageItemProps {
  titleKey: string;
  descriptionKey: string;
  checkedIconSrc: string;
  uncheckedIconSrc: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  isLoadingPrice: boolean;
  infoTextKey?: string;
}

const CoverageItem: React.FC<CoverageItemProps> = ({
  titleKey,
  descriptionKey,
  checkedIconSrc,
  uncheckedIconSrc,
  checked,
  onCheckedChange,
  isLoadingPrice,
  infoTextKey,
}) => {
  const { t } = useTranslation();

  const currentIconSrc = checked ? checkedIconSrc : uncheckedIconSrc;

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between p-4 rounded-lg bg-secondary/10 space-y-4 md:space-y-0">
      <div className="flex items-center space-x-4 w-full md:w-auto">
        <div className="relative flex h-12 w-12 shrink-0 overflow-hidden rounded-full bg-white border border-primary-foreground/20 items-center justify-center">
          <img src={currentIconSrc} alt={t(titleKey)} className="h-11 w-11" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 ">
            <Label className="text-lg font-medium leading-none text-primary">
              {t(titleKey)}
            </Label>
            {infoTextKey && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-primary hover:bg-primary-foreground/10">
                    <Info className="h-4 w-4" />
                    <span className="sr-only">{t("common.info")}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t(titleKey)}</DialogTitle>
                    <DialogDescription
                      dangerouslySetInnerHTML={{ __html: t(infoTextKey) }}
                    />
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t(descriptionKey) }} />
        </div>
      </div>
      <Button
        variant="outline"
        className={cn(
          "w-full md:w-auto rounded-full px-6 py-2",
          checked ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-primary border-primary'
        )}
        onClick={() => onCheckedChange(!checked)}
        disabled={isLoadingPrice}
      >
        {checked ? t("common.remove") : t("common.add")}
      </Button>
    </div>
  );
};

export default CoverageItem;
